/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { examApi, examFolderApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Exam, ExamAssignment, ExamFolder } from '../types';
import Questions from './Questions';
import Evaluations from './Evaluations';
import ExamsTable from '../components/ExamsTable';

type TeacherTab = 'exams' | 'questions' | 'assignments' | 'drafts' | 'evaluations';

const TeacherDashboard: React.FC = () => {
    const [folderTree, setFolderTree] = useState<ExamFolder[]>([]);
    const [foldersLoading, setFoldersLoading] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [newFolderParentId, setNewFolderParentId] = useState<number | null>(null);
    const [exams, setExams] = useState<Exam[]>([]);
    const [drafts, setDrafts] = useState<Exam[]>([]);
    const [assignments, setAssignments] = useState<ExamAssignment[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TeacherTab>('exams');
    const [selectedClass, setSelectedClass] = useState<string>('Open');
    // Folders-first UX: null = root folders view; 'default' = synthetic Default folder; number = real folder id
    const [selectedFolder, setSelectedFolder] = useState<number | 'default' | null>(null);
    const { user } = useAuth();

    const loadFolders = async () => {
        if (!user?.id) return;
        try {
            setFoldersLoading(true);
            const tree = await examFolderApi.getTree(user.id);
            setFolderTree(tree);
        } catch (e) {
            console.error('Failed to load folders', e);
        } finally {
            setFoldersLoading(false);
        }
    };

    const createFolder = async () => {
        if (!user?.id) return;
        const name = newFolderName.trim();
        if (!name) { alert('Please enter a folder name'); return; }
        try {
            await examFolderApi.create(user.id, name, newFolderParentId ?? null);
            setNewFolderName('');
            setNewFolderParentId(null);
            await loadFolders();
        } catch (e) {
            console.error('Failed to create folder', e);
            alert('Could not create folder.');
        }
    };

    const moveExamToFolder = async (examId: number, folderId: number | null) => {
        if (!user?.id) return;
        try {
            await examFolderApi.moveExam(user.id, examId, folderId ?? null);
            // Refresh exams list to reflect folder assignment
            const list = await examApi.getByTeacher(user.id);
            setExams((list || []).filter((e: any) => e.status !== 'DRAFT'));
        } catch (e) {
            console.error('Failed to move exam', e);
            alert('Could not move exam to folder.');
        }
    };

    const flattenFolders = (nodes: ExamFolder[], level = 0): { id: number | null; label: string }[] => {
        const res: { id: number | null; label: string }[] = [];
        for (const n of nodes) {
            res.push({ id: n.id, label: `${'— '.repeat(level)}${n.name}` });
            if (n.children && n.children.length > 0) {
                res.push(...flattenFolders(n.children, level + 1));
            }
        }
        return res;
    };

    const findFolderById = (nodes: ExamFolder[], id: number): ExamFolder | null => {
        for (const n of nodes) {
            if (n.id === id) return n;
            if (n.children && n.children.length > 0) {
                const found = findFolderById(n.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const handleRenameFolder = async (folderId: number, currentName?: string) => {
        if (!user?.id) return;
        const proposed = window.prompt('Enter new folder name', currentName ?? '');
        if (proposed == null) return; // cancelled
        const name = proposed.trim();
        if (!name) { alert('Folder name cannot be empty'); return; }
        try {
            await examFolderApi.rename(user.id, folderId, name);
            await loadFolders();
        } catch (e) {
            console.error('Failed to rename folder', e);
            alert('Could not rename folder.');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (user?.id) {
                    const [examsData, assignmentsData, draftsData] = await Promise.all([
                        examApi.getByTeacher(user.id),
                        examApi.getAssignmentsByTeacher(user.id),
                        examApi.getDraftsByTeacher(user.id),
                    ]);
                    // Exclude drafts from the published exams list
                    setExams((examsData || []).filter(e => e.status !== 'DRAFT'));
                    setAssignments(assignmentsData || []);
                    setDrafts(draftsData || []);
                }
            } catch (error) {
                console.error('Error fetching teacher dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        loadFolders();
    }, [user?.id]);

    const handleDeleteExam = async (examId: number) => {
        if (window.confirm('Are you sure you want to delete this exam?')) {
            try {
                await examApi.delete(examId);
                setExams(exams.filter((e) => e.id !== examId));
            } catch (error) {
                console.error('Error deleting exam:', error);
            }
        }
    };

    const handlePublishDraft = async (examId: number) => {
        if (!user?.id) return;
        if (!window.confirm('Publish this draft? Students will be able to see/attempt it once active.')) return;
        try {
            await examApi.publish(examId, user.id);
            // Refresh lists
            const [examsData, assignmentsData, draftsData] = await Promise.all([
                examApi.getByTeacher(user.id),
                examApi.getAssignmentsByTeacher(user.id),
                examApi.getDraftsByTeacher(user.id),
            ]);
            setExams((examsData || []).filter(e => e.status !== 'DRAFT'));
            setAssignments(assignmentsData || []);
            setDrafts(draftsData || []);
            setActiveTab('exams');
        } catch (error) {
            console.error('Error publishing draft exam:', error);
            alert('Failed to publish draft. Please ensure all required fields and questions are complete.');
        }
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div data-testid="teacher-dashboard">
            <div className="card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    👩‍🏫 Welcome, {user?.name}!
                </h1>
                <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                    Manage your exams and help students learn! 📚
                </p>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '2px solid #e2e8f0', flexWrap: 'wrap' }}>
                <button
                    onClick={() => setActiveTab('exams')}
                    className={`btn btn-sm ${activeTab === 'exams' ? 'btn-primary' : 'btn-outline'}`}
                    type="button"
                >
                    📋 Your Exams ({exams.length})
                </button>
                <button
                    onClick={() => setActiveTab('questions')}
                    className={`btn btn-sm ${activeTab === 'questions' ? 'btn-primary' : 'btn-outline'}`}
                    type="button"
                >
                    ❓ Questions
                </button>
                <button
                    onClick={() => setActiveTab('assignments')}
                    className={`btn btn-sm ${activeTab === 'assignments' ? 'btn-primary' : 'btn-outline'}`}
                    type="button"
                >
                    🧾 Assigned Exams ({assignments.length})
                </button>
                <button
                    onClick={() => setActiveTab('drafts')}
                    className={`btn btn-sm ${activeTab === 'drafts' ? 'btn-primary' : 'btn-outline'}`}
                    type="button"
                >
                    🗂 Drafts ({drafts.length})
                </button>
                <button
                    onClick={() => setActiveTab('evaluations')}
                    className={`btn btn-sm ${activeTab === 'evaluations' ? 'btn-primary' : 'btn-outline'}`}
                    type="button"
                >
                    🧮 Evaluations
                </button>
            </div>

            {/* Questions Tab (inline) */}
            {activeTab === 'questions' && (
                <div>
                    <Questions />
                </div>
            )}

            {/* Evaluations Tab (inline) */}
            {activeTab === 'evaluations' && (
                <div>
                    <Evaluations />
                </div>
            )}

            {/* Exams Tab */}
            {activeTab === 'exams' && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            📋 Your Exams
                        </h2>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div className="form-control" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <select
                                    className="form-input"
                                    value={String(newFolderParentId ?? 'null')}
                                    onChange={(e) => setNewFolderParentId(e.target.value === 'null' ? null : Number(e.target.value))}
                                    title="Parent folder (optional)"
                                    style={{ height: '55px' }}
                                >
                                    <option value="null">Root folder</option>
                                    {flattenFolders(folderTree).map(opt => (
                                        <option key={String(opt.id)} value={String(opt.id)}>{opt.label}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="New folder name"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    style={{ minWidth: 200, height: '40px' }}
                                />
                                <button className="btn btn-outline" onClick={createFolder} disabled={foldersLoading}>
                                    {foldersLoading ? 'Creating...' : '➕ Create Folder'}
                                </button>
                            </div>
                            <Link to="/create-exam" className="btn btn-primary">
                                ➕ Create New Exam
                            </Link>
                        </div>
                    </div>

                    {/* Folders-first view */}
                    {(() => {
                        const unassigned = (exams || []).filter(e => !e.folderId);
                        const renderFolderTile = (label: string, onClick: () => void, emoji: string = '📁', actions?: React.ReactNode) => (
                            <div
                                key={label}
                                onClick={onClick}
                                className="card bg-base-100 shadow hover:shadow-md transition btn btn-ghost"
                                style={{ textAlign: 'left', padding: '1rem', minWidth: 220, position: 'relative', cursor: 'pointer' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <div style={{ fontSize: '1.5rem' }}>{emoji}</div>
                                        <div style={{ fontWeight: 600 }}>{label}</div>
                                    </div>
                                    {actions && (
                                        <div onClick={(e) => e.stopPropagation()}>{actions}</div>
                                    )}
                                </div>
                            </div>
                        );

                        if (selectedFolder === null) {
                            const roots = folderTree;
                            const hasAny = (roots && roots.length > 0) || unassigned.length > 0;
                            if (!hasAny) {
                                return (
                                    <div className="empty-state">
                                        <div className="empty-state-emoji">🗂</div>
                                        <h3 className="empty-state-title">No Folders Yet</h3>
                                        <p>Create a folder or an exam to get started.</p>
                                    </div>
                                );
                            }
                            return (
                                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                    {renderFolderTile('Default', () => setSelectedFolder('default'), '📦')}
                                    {roots.map(f => (
                                        renderFolderTile(
                                            f.name,
                                            () => setSelectedFolder(f.id),
                                            '📁',
                                            <button className="btn btn-ghost btn-xs" title="Rename folder" onClick={() => handleRenameFolder(f.id, f.name)}>✏️</button>
                                        )
                                    ))}
                                </div>
                            );
                        }

                        // Inside a folder (real or Default)
                        const isDefault = selectedFolder === 'default';
                        const currentFolder = !isDefault && typeof selectedFolder === 'number' ? findFolderById(folderTree, selectedFolder) : null;
                        const childFolders: ExamFolder[] = isDefault ? [] : (currentFolder?.children || []);
                        const filteredExams = isDefault
                            ? (exams || []).filter(e => !e.folderId)
                            : (exams || []).filter(e => e.folderId === selectedFolder);

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <button className="btn btn-sm btn-outline" onClick={() => setSelectedFolder(null)}>← All Folders</button>
                                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {isDefault ? '📦 Default' : `📁 ${currentFolder?.name ?? 'Folder'}`}
                                    </h3>
                                    {!isDefault && currentFolder && (
                                        <button className="btn btn-outline btn-sm" onClick={() => handleRenameFolder(currentFolder.id, currentFolder.name)}>
                                            ✏️ Rename Folder
                                        </button>
                                    )}
                                </div>

                                {childFolders.length > 0 && (
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Subfolders</div>
                                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                            {childFolders.map(cf => (
                                                renderFolderTile(
                                                    cf.name,
                                                    () => setSelectedFolder(cf.id),
                                                    '📁',
                                                    <button className="btn btn-ghost btn-xs" title="Rename folder" onClick={() => handleRenameFolder(cf.id, cf.name)}>✏️</button>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {filteredExams.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-state-emoji">📝</div>
                                        <h3 className="empty-state-title">No Exams in this folder</h3>
                                        <p>Move exams here using the Move control, or create a new one.</p>
                                    </div>
                                ) : (
                                    <ExamsTable
                                        exams={filteredExams}
                                        showCategory={true}
                                        showStatus={true}
                                        showEdit={true}
                                        showAssign={true}
                                        showEvaluate={true}
                                        showPreview={true}
                                        showDownload={true}
                                        folderOptions={flattenFolders(folderTree)}
                                        onMoveToFolder={(examId, folderId) => moveExamToFolder(examId, folderId)}
                                        onDelete={(id) => handleDeleteExam(id)}
                                    />
                                )}
                            </div>
                        );
                    })()}
                </>
            )}

            {/* Assignments Tab */}
            {activeTab === 'assignments' && (
                <div>
                    <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        🧾 Exams You Assigned to Students
                    </h2>

                    {assignments.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-emoji">📭</div>
                            <h3 className="empty-state-title">No Assignments Yet</h3>
                            <p>Assign your exams to students to see them listed here.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Exam</th>
                                        <th>Student</th>
                                        <th>Username</th>
                                        <th>Email</th>
                                        <th>Assigned At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignments.map((a) => (
                                        <tr key={a.assignmentId}>
                                            <td>{a.examTitle}</td>
                                            <td>{a.studentName}</td>
                                            <td>{a.studentUsername}</td>
                                            <td>{a.studentEmail}</td>
                                            <td>{a.assignedAt ? new Date(a.assignedAt).toLocaleString() : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Drafts Tab */}
            {activeTab === 'drafts' && (
                <div>
                    <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        🗂 Your Draft Exams
                    </h2>

                    {drafts.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-emoji">🗂</div>
                            <h3 className="empty-state-title">No Drafts Yet</h3>
                            <p>Use "Save as Draft" while creating an exam to keep working on it later.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Duration</th>
                                        <th>Total Marks</th>
                                        <th>Updated</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drafts.map((exam) => (
                                        <tr key={exam.id}>
                                            <td>
                                                <strong>{exam.title}</strong>
                                                <div style={{ fontSize: '0.875rem', color: '#718096' }}>Draft</div>
                                            </td>
                                            <td>{exam.durationMinutes ?? '-'}</td>
                                            <td>{exam.totalMarks ?? '-'}</td>
                                            <td>{exam.createdAt ? new Date(exam.createdAt).toLocaleDateString() : '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <Link to={`/edit-exam/${exam.id}`} className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }}>
                                                        ✏️ Resume Editing
                                                    </Link>
                                                    <button className="btn btn-primary btn-sm" onClick={() => handlePublishDraft(exam.id!)} style={{ whiteSpace: 'nowrap' }}>
                                                        🚀 Publish
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeleteExam(exam.id!)}
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        🗑 Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TeacherDashboard;